'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownLong } from '@fortawesome/pro-solid-svg-icons';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { HighlightedCriteria } from 'src/components/Strategy/shared/HighlightedCriteria';
import {
  buildExecutionStepCriteria,
  formatCriteriaTemplate,
} from 'src/utils/criteriaDisplayUtils/criteriaDisplayUtils';
type StrategyMode = 'target' | 'engage';
interface StrategyExecutionSummaryProps {
  strategyType?: StrategyMode;
}

const StrategyExecutionSummary: React.FC<StrategyExecutionSummaryProps> = ({
  strategyType,
}) => {
  const { strategyDetails } = useStrategyForm(strategyType);
  const executionSummary = strategyDetails?.execution_summary;
  const queryPlan = strategyDetails?.query_plan;

  // Swap the ids the summary mentions for names from the query plan's ui_metadata
  const steps = useMemo(
    () =>
      (executionSummary?.steps ?? []).map((step) => {
        const criteria = buildExecutionStepCriteria(step, queryPlan?.steps);
        return {
          ...criteria,
          text: formatCriteriaTemplate(
            criteria.criteriaTemplate,
            criteria.criteriaInputs
          ),
        };
      }),
    [executionSummary, queryPlan]
  );
  return (
    <div
      className="flex flex-col gap-4 mx-auto w-full"
      data-testid="strategy-execution-summary"
      aria-label="Strategy execution summary"
    >
      <div
        className="flex flex-col gap-6 bg-white rounded-xl border
      border-neutral-200 shadow-sm p-6"
        data-testid="strategy-execution-summary-card"
      >
        <div className="flex flex-col gap-1">
          <h2
            className="justify-start text-primary text-xl font-bold font-['Inter'] leading-7"
            data-testid="strategy-execution-summary-title"
          >
            Execution Summary
          </h2>
          <p
            className="self-stretch justify-start text-tertiary text-sm font-normal font-['Inter'] leading-tight"
            data-testid="strategy-execution-summary-description"
          >
            Overview of the strategy execution steps that create these columns.
          </p>
        </div>
        {executionSummary ? (
          <div
            className="flex flex-col gap-4"
            data-testid="strategy-execution-steps"
            role="list"
            aria-label="Execution steps"
          >
            {strategyDetails.execution_summary?.coalesce && (
              <>
                <div
                  className="self-stretch inline-flex flex-col justify-start items-start gap-1"
                  data-testid="strategy-execution-coalesce"
                  role="listitem"
                  aria-label="Coalesce criteria"
                >
                  <div
                    className="justify-start text-primary text-sm font-bold font-['Inter'] leading-5"
                    data-testid="strategy-execution-coalesce-label"
                  >
                    Coalesce
                  </div>
                  <div className="self-stretch flex flex-col gap-1">
                    <div
                      className="w-full text-primary text-sm font-normal font-['Inter'] leading-5"
                      data-testid="strategy-execution-coalesce-description"
                    >
                      {strategyDetails.execution_summary.coalesce.description}
                    </div>
                    <div
                      className="w-full text-tertiary text-sm font-normal font-['Inter'] leading-5"
                      data-testid="strategy-execution-coalesce-sort"
                    >
                      {strategyDetails.execution_summary.coalesce.sort}
                    </div>
                  </div>
                </div>
                <div className="flex justify-start" aria-hidden="true">
                  <FontAwesomeIcon
                    icon={faArrowDownLong}
                    className="text-neutral-200 text-sm"
                  />
                </div>
              </>
            )}
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className="flex justify-start" aria-hidden="true">
                    <FontAwesomeIcon
                      icon={faArrowDownLong}
                      className="text-neutral-200 text-sm"
                    />
                  </div>
                )}
                <div
                  className="self-stretch inline-flex flex-col justify-start items-start gap-1"
                  data-testid={`strategy-execution-step-${index + 1}`}
                  role="listitem"
                  aria-label={`Step ${index + 1}: ${step.text}`}
                >
                  <div
                    className="justify-start text-primary text-sm font-bold font-['Inter'] leading-5"
                    data-testid={`strategy-execution-step-${index + 1}-label`}
                  >
                    Step {index + 1}
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-1.5 flex-wrap content-start">
                    <div
                      className="w-full justify-center text-primary text-sm font-normal font-['Inter'] leading-5"
                      data-testid={`strategy-execution-step-${
                        index + 1
                      }-content`}
                    >
                      <HighlightedCriteria
                        criteriaTemplate={step.criteriaTemplate}
                        criteriaInputs={step.criteriaInputs}
                        className="text-primary text-sm leading-5"
                        linkifyCodes={false}
                      />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div
            data-testid="strategy-execution-summary-empty"
            aria-label="No execution summary available"
          >
            No execution summary available.
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyExecutionSummary;
